// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { FolderOpen, Lock, Mail, User, BookOpen, Wrench, Shield, FileCheck, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register form state
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const userData = await login(loginEmail, loginPassword);
      toast.success('Login realizado com sucesso!');
      navigate(userData?.must_change_password ? '/minha-conta' : '/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao fazer login';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await register(registerName, registerEmail, registerPassword);
      toast.success('Conta criada com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao criar conta';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen w-full">
      {/* Left Panel - Form */}
      <div className="flex items-center justify-center p-6 sm:p-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="JW Donate Audit Logo" 
              className="w-14 h-14 object-contain"
            />
            <span className="font-extrabold text-2xl tracking-tight text-slate-900 font-heading">
              JW Donate Audit
            </span>
          </div>

          <div className="space-y-2">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 font-heading">
              Bem-vindo
            </h1>
            <p className="text-slate-500 text-base leading-relaxed">
              Gerencie seus documentos de forma organizada e segura
            </p>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-slate-100 p-1.5 rounded-xl h-12">
              <TabsTrigger 
                value="login" 
                data-testid="login-tab"
                className="rounded-lg font-medium text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 transition-all"
              >
                Entrar
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                data-testid="register-tab"
                className="rounded-lg font-medium text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 transition-all"
              >
                Criar Conta
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-8 animate-fade-in">
              <Card className="border-0 shadow-none bg-transparent">
                <CardContent className="p-0 space-y-5">
                  <form onSubmit={handleLogin} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-slate-700 font-medium">Email</Label>
                      <div className="relative group">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-slate-600 transition-colors" />
                        <Input
                          id="login-email"
                          data-testid="login-email-input"
                          type="email"
                          placeholder="seu@email.com"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          className="pl-11 h-12 rounded-xl border-slate-200 bg-white focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-all"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className="text-slate-700 font-medium">Senha</Label>
                      <div className="relative group">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-slate-600 transition-colors" />
                        <Input
                          id="login-password"
                          data-testid="login-password-input"
                          type="password"
                          placeholder="********"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          className="pl-11 h-12 rounded-xl border-slate-200 bg-white focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-all"
                          required
                        />
                      </div>
                    </div>

                    <div className="text-right">
                      <Link 
                        to="/forgot-password" 
                        className="text-sm text-slate-500 hover:text-slate-900 transition-colors font-medium"
                        data-testid="forgot-password-link"
                      >
                        Esqueci minha senha
                      </Link>
                    </div>

                    <Button 
                      type="submit" 
                      data-testid="login-submit-button"
                      className="w-full h-12 bg-slate-900 hover:bg-slate-800 rounded-xl font-semibold text-base shadow-lg shadow-slate-900/20 hover:shadow-xl hover:shadow-slate-900/30 transition-all duration-200 active:scale-[0.98]"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Entrando...' : 'Entrar'}
                      {!isLoading && <ArrowRight className="w-4 h-4 ml-2" />}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register" className="mt-8 animate-fade-in">
              <Card className="border-0 shadow-none bg-transparent">
                <CardContent className="p-0 space-y-5">
                  <form onSubmit={handleRegister} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="register-name" className="text-slate-700 font-medium">Nome</Label>
                      <div className="relative group">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-slate-600 transition-colors" />
                        <Input
                          id="register-name"
                          data-testid="register-name-input"
                          type="text"
                          placeholder="Seu nome completo"
                          value={registerName}
                          onChange={(e) => setRegisterName(e.target.value)}
                          className="pl-11 h-12 rounded-xl border-slate-200 bg-white focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-all"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="register-email" className="text-slate-700 font-medium">Email</Label>
                      <div className="relative group">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-slate-600 transition-colors" />
                        <Input
                          id="register-email"
                          data-testid="register-email-input"
                          type="email"
                          placeholder="seu@email.com"
                          value={registerEmail}
                          onChange={(e) => setRegisterEmail(e.target.value)}
                          className="pl-11 h-12 rounded-xl border-slate-200 bg-white focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-all"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-password" className="text-slate-700 font-medium">Senha</Label>
                      <div className="relative group">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-slate-600 transition-colors" />
                        <Input
                          id="register-password"
                          data-testid="register-password-input"
                          type="password"
                          placeholder="Mínimo 8 caracteres"
                          value={registerPassword}
                          onChange={(e) => setRegisterPassword(e.target.value)}
                          className="pl-11 h-12 rounded-xl border-slate-200 bg-white focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-all"
                          required
                          minLength={8}
                        />
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      data-testid="register-submit-button"
                      className="w-full h-12 bg-slate-900 hover:bg-slate-800 rounded-xl font-semibold text-base shadow-lg shadow-slate-900/20 hover:shadow-xl hover:shadow-slate-900/30 transition-all duration-200 active:scale-[0.98]"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Criando conta...' : 'Criar Conta'}
                      {!isLoading && <ArrowRight className="w-4 h-4 ml-2" />}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Right Panel - Image with Glassmorphism cards */}
      <div className="hidden lg:block relative overflow-hidden">
        {/* Background image with overlay */}
        <div className="absolute inset-0 login-background" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-800/85 to-congregation/60" />
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-72 h-72 bg-congregation/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-maintenance/15 rounded-full blur-3xl" />
        
        {/* Content */}
        <div className="relative h-full flex flex-col items-center justify-center p-12">
          <div className="text-center text-white space-y-8 max-w-lg animate-fade-in">
            <h2 className="text-4xl xl:text-5xl font-extrabold tracking-tight font-heading leading-tight">
              Gestão de Auditoria<br />
              <span className="text-congregation-border">das Contas</span>
            </h2>
            
            {/* Feature cards with glassmorphism */}
            <div className="grid grid-cols-2 gap-4 pt-6">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all duration-300 group">
                <div className="w-10 h-10 bg-congregation/30 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-5 h-5 text-congregation-border" />
                </div>
                <p className="font-semibold text-white text-sm">Contas da Congregação</p>
                <p className="text-slate-400 text-xs mt-1">Documentos financeiros</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all duration-300 group">
                <div className="w-10 h-10 bg-maintenance/30 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Wrench className="w-5 h-5 text-maintenance-border" />
                </div>
                <p className="font-semibold text-white text-sm">Contas da Manutenção</p>
                <p className="text-slate-400 text-xs mt-1">Infraestrutura e reparos</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all duration-300 group">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <p className="font-semibold text-white text-sm">Acesso Seguro</p>
                <p className="text-slate-400 text-xs mt-1">Controle de permissões</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all duration-300 group">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <FileCheck className="w-5 h-5 text-white" />
                </div>
                <p className="font-semibold text-white text-sm">Auditoria Facilitada</p>
                <p className="text-slate-400 text-xs mt-1">Organização trimestral</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
